import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import { firstValueFrom } from 'rxjs';
import { DashboardHeaderComponent } from '../structure/header/header';
import { DashboardSidebarComponent } from '../structure/sidebar/sidebar';
import { ServerTimeService } from '../../services/server-time';
import { AppPreferencesService } from '../../services/app-preferences';
import { Httpservice } from '../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../helper/apiconstant';
import { DEFAULT_DASHBOARD_USER } from '../dashboard/dashboard.data';
import { ClickOutsideDirective } from '../../helper/directives/click-outside.directive';
import { DashboardUserProfile } from '../dashboard/dashboard.interfaces';

type VoltageFilter = 'all' | number;

interface DonutSlice {
  name: string;
  value: number;
  color: string;
  percentage?: number;
}

interface TransmissionLine {
  id: number;
  line: string;
  kv: number;
  from: string;
  to: string;
  mwFlow: number;
}

interface TransmissionLinesLoading {
  voltage_levels: number[];
  lines: Array<{
    id: number;
    line: string;
    kv: number;
    from: string;
    to: string;
    mw_flow: number;
  }>;
}

interface CrossborderLineExchangeDetails {
  lines: Array<{
    id: number;
    interconnection: string;
    voltage: string;
    direction: 'Import' | 'Export';
    mw_flow: number;
  }>;
}

interface CrossborderLineExchangeRow {
  name: string;
  voltage: string;
  direction: 'Import' | 'Export';
  actual: number;
  variance: number;
}

interface GridKpiCard {
  key: string;
  icon: string;
  label: string;
  value: number | string | null;
  unit: string;
  note: string;
  tone: string;
  badge: string | null;
  market_date?: string | null;
  till_block?: number | null;
  market_time_range?: string | null;
  comparison?: { direction: 'up' | 'down' | 'same'; difference: number } | null;
}

interface GridKpiOverview {
  title: string;
  data_date: string | null;
  updated_at: string | null;
  cards: GridKpiCard[];
}

interface AdminDashboardApiResponse<T> {
  value?: T;
}

interface InstalledCapacityMix {
  total_mw: number;
  categories: Array<{
    name: string;
    capacity_mw: number;
    percentage: number;
  }>;
}

interface DailyGenerationSourceMix {
  total_mw: number;
  categories: Array<{
    name: string;
    generation_mw: number;
    percentage: number;
  }>;
}

interface DailyGenerationOwnerMix {
  total_mw: number;
  categories: Array<{
    name: string;
    generation_mw: number;
    percentage: number;
  }>;
}

interface GenerationTrend {
  data_date: string | null;
  categories: string[];
  series: Array<{
    name: string;
    values: number[];
  }>;
}

interface DailyEnergyExchange {
  data_date: string | null;
  categories: string[];
  import: number[];
  export: number[];
  net: number[];
  summary: { import_mw: number; export_mw: number; net_mw: number };
}

interface LoadCurve {
  data_date: string | null;
  last_synced_block_no?: number;
  block_numbers?: number[];
  categories: string[];
  national_demand: Array<number | null>;
  system_demand: Array<number | null>;
}

interface ProvinceLoadDistribution {
  data_date: string | null;
  total_system_load_mw: number;
  provinces: Array<{ name: string; mw: number; percentage: number }>;
}

interface BsCalendarCell {
  bs_date: string;
  bs_date_label: string;
  ad_date: string;
  in_current_month: boolean;
  is_today: boolean;
  is_selected: boolean;
}

interface BsCalendar {
  bs_year: number;
  bs_month: number;
  bs_month_name: string;
  cells: BsCalendarCell[];
}

type DashboardDateFilterType = 'source' | 'owner' | 'load' | 'trend' | 'exchange' | 'province';

interface DailyGenerationDateFilter {
  date: string;
  bsLabel: string;
  calendarOpen: boolean;
  calendarLoading: boolean;
  calendar: BsCalendar | null;
}

const CHART_HEIGHT = 250;
const MEGAWATT_UNIT = 'MW';
const CAPACITY_MIX_COLORS: Record<string, string> = {
  Hydro: '#3b82f6',
  Solar: '#f59e0b',
  Thermal: '#ef4444',
  Other: '#a78bfa',
};
const DAILY_GENERATION_SOURCE_COLORS: Record<string, string> = {
  Hydro: '#3b82f6',
  Solar: '#f59e0b',
  Thermal: '#ef4444',
  Others: '#a78bfa',
};
const DAILY_GENERATION_OWNER_COLORS: Record<string, string> = {
  NEA: '#06b6d4',
  'NEA Subs': '#6366f1',
  IPP: '#10b981',
};

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, DashboardSidebarComponent, DashboardHeaderComponent, ClickOutsideDirective],
  templateUrl: './admin-dashboard.html',
  styleUrl: './admin-dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDashboard implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('capacityChart') private readonly capacityChartElement?: ElementRef<HTMLDivElement>;
  @ViewChild('fuelChart') private readonly fuelChartElement?: ElementRef<HTMLDivElement>;
  @ViewChild('sourceChart') private readonly sourceChartElement?: ElementRef<HTMLDivElement>;
  @ViewChild('loadChart') private readonly loadChartElement?: ElementRef<HTMLDivElement>;
  @ViewChild('generationChart') private readonly generationChartElement?: ElementRef<HTMLDivElement>;
  @ViewChild('exchangeChart') private readonly exchangeChartElement?: ElementRef<HTMLDivElement>;
  @ViewChild('loadDemandSection') private readonly loadDemandSectionElement?: ElementRef<HTMLElement>;
  @ViewChild('generationEnergySection') private readonly generationEnergySectionElement?: ElementRef<HTMLElement>;
  @ViewChild('transmissionSection') private readonly transmissionSectionElement?: ElementRef<HTMLElement>;

  protected voltageFilter: VoltageFilter = 'all';
  protected voltageFilters: VoltageFilter[] = ['all'];
  protected readonly user: DashboardUserProfile = DEFAULT_DASHBOARD_USER;
  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected tickerIndex = 1;
  protected readonly alerts = [
    { severity: 'critical', title: 'Transmission line loading above 90%', detail: 'Dhalkebar-Muzaffarpur 400kV', time: '34:33' },
    { severity: 'critical', title: 'SCADA communication failure', detail: 'RTU · Dhalkebar', time: '24:29' },
    { severity: 'warning', title: 'Generator availability reduced', detail: 'Butwal-III Unit 2', time: '24:13' },
    { severity: 'warning', title: 'Overload detected on 132kV circuit', detail: 'Hetauda-Dhampus Link', time: '24:11' },
    { severity: 'warning', title: 'SCADA data delay detected', detail: 'RTU · Raxaul 55', time: '14:39' },
    { severity: 'info', title: 'Generator availability update received', detail: 'Upper Tamakoshi NPP', time: '24:31' },
    { severity: 'info', title: 'Weather forecast updated', detail: 'DHM Weather Service', time: '24:35' },
  ];
  protected kpis: GridKpiCard[] = [];
  protected gridKpiTitle = '';
  protected gridKpiDateLabel = '';
  protected gridKpiDataDate = '';
  protected gridKpiUpdatedTime = '';
  /* Static UI-reference data is intentionally not rendered. The KPI values come from the admin dashboard API.
    { icon: '⌁', label: 'System Frequency', value: '49.95', unit: 'Hz', note: '49.8 – 50.2 Hz', tone: 'frequency', badge: 'Normal' },
    { icon: '☁', label: 'Weather · KTM', value: '24', unit: '°C', note: 'Partly cloudy', tone: 'weather', badge: '' },
    { icon: '◷', label: 'SCADA Sync', value: '14:05:50', unit: '', note: 'Quality: 99.8%', tone: 'sync', badge: 'Live' },
    { icon: 'ϟ', label: 'Total Generation', value: '1,245', unit: 'MW', note: '18,450 MWh today', tone: 'generation', badge: '' },
    { icon: '〽', label: 'System Peak', value: '1,380', unit: 'MW', note: '↑ +4.8% yesterday', tone: 'system', badge: '' },
    { icon: '↗', label: 'National Peak', value: '1,420', unit: 'MW', note: '↑ +3.2% yesterday', tone: 'national', badge: '' },
    { icon: '↓', label: 'Import', value: '185', unit: 'MW', note: 'India → Nepal', tone: 'import', badge: 'Active' },
    { icon: '↑', label: 'Export', value: '42', unit: 'MW', note: 'Nepal → India', tone: 'export', badge: 'Active' },
    { icon: '↔', label: 'Net Import', value: '143', unit: 'MW', note: 'Interconnection', tone: 'net', badge: '' },
    { icon: 'Rs', label: 'DAM Price', value: '8.42', unit: 'NPR/kWh', note: 'Day Ahead Market', tone: 'dam', badge: '' },
    { icon: 'Rs', label: 'GDAM Price', value: '7.96', unit: 'NPR/kWh', note: 'Green DAM', tone: 'gdam', badge: '' },
    { icon: 'Rs', label: 'RTM Price', value: '8.71', unit: 'NPR/kWh', note: 'Real Time Market', tone: 'rtm', badge: '' },
  */
  protected provinces: ProvinceLoadDistribution['provinces'] = [];
  protected provinceTotalSystemLoadMw = 0;
  protected transmissionLines: TransmissionLine[] = [];
  protected tieLines: CrossborderLineExchangeRow[] = [];
  protected capacityMix: DonutSlice[] = [];
  protected installedCapacityTotalMw = 0;
  protected dailyGenerationSourceMix: DonutSlice[] = [];
  protected dailyGenerationSourceTotalMw = 0;
  protected dailyGenerationOwnerMix: DonutSlice[] = [];
  protected dailyGenerationOwnerTotalMw = 0;
  protected readonly sourceMixDateFilter: DailyGenerationDateFilter = this.createMixDateFilter();
  protected readonly ownerMixDateFilter: DailyGenerationDateFilter = this.createMixDateFilter();
  protected readonly loadCurveDateFilter: DailyGenerationDateFilter = this.createMixDateFilter();
  protected readonly generationTrendDateFilter: DailyGenerationDateFilter = this.createMixDateFilter();
  protected readonly energyExchangeDateFilter: DailyGenerationDateFilter = this.createMixDateFilter();
  protected readonly provinceDateFilter: DailyGenerationDateFilter = this.createMixDateFilter();
  protected loadCurve: LoadCurve = { data_date: null, last_synced_block_no: 0, block_numbers: [], categories: [], national_demand: [], system_demand: [] };
  protected generationTrend: GenerationTrend = { data_date: null, categories: [], series: [] };
  protected dailyEnergyExchange: DailyEnergyExchange = {
    data_date: null,
    categories: [],
    import: [],
    export: [],
    net: [],
    summary: { import_mw: 0, export_mw: 0, net_mw: 0 },
  };
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  private readonly charts: Highcharts.Chart[] = [];
  private capacityMixChart?: Highcharts.Chart;
  private dailyGenerationSourceMixChart?: Highcharts.Chart;
  private dailyGenerationOwnerMixChart?: Highcharts.Chart;
  private loadCurveChart?: Highcharts.Chart;
  private generationTrendChart?: Highcharts.Chart;
  private energyExchangeChart?: Highcharts.Chart;
  private dashboardSectionObserver?: IntersectionObserver;
  private readonly loadedDashboardRequests = new Set<string>();
  private readonly pendingDashboardRequestIds = new Map<string, number>();
  private readonly latestDashboardRequestIds = new Map<string, number>();
  private nextDashboardRequestId = 0;
  private viewInitialized = false;
  private readonly bsDateLabels = new Map<string, string>();

  constructor(
    private readonly injector: Injector,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly preferencesSvc: AppPreferencesService,
    private readonly httpSvc: Httpservice,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  protected get currentAlert(): (typeof this.alerts)[number] {
    return this.alerts[this.tickerIndex];
  }

  protected get currentTimeLabel(): string {
    return this.serverTimeSvc.getCurrentTimeLabel();
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  ngOnInit(): void {
    void this.syncDateFilterLabels();
    this.loadInitialDashboardCards();
  }

  protected get filteredLines(): TransmissionLine[] {
    if (this.voltageFilter === 'all') {
      return this.transmissionLines.slice(0, 5);
    }

    return this.transmissionLines
      .filter((line) => line.kv === this.voltageFilter)
      .slice(0, 5);
  }

  ngAfterViewInit(): void {
    afterNextRender(() => {
      this.viewInitialized = true;
      this.renderCharts();
      this.observeDeferredDashboardSections();
    }, { injector: this.injector });
  }

  ngOnDestroy(): void {
    this.dashboardSectionObserver?.disconnect();
    this.charts.forEach((chart) => chart.destroy());
  }

  @HostListener('window:resize')
  protected reflowCharts(): void {
    this.charts.forEach((chart) => chart.reflow());
    this.positionOpenMixCalendar();
  }

  @HostListener('window:scroll')
  protected repositionMixCalendar(): void {
    this.positionOpenMixCalendar();
  }

  protected handleDashboardScroll(): void {
    document.querySelectorAll<HTMLInputElement>('input[type="date"]:focus').forEach((input) => input.blur());
    this.positionOpenMixCalendar();
  }

  @HostListener('window:app-preferences-change')
  protected refreshGridKpiDateLabel(): void {
    this.updateGridKpiDateLabel();
    this.sourceMixDateFilter.calendarOpen = false;
    this.ownerMixDateFilter.calendarOpen = false;
    this.loadCurveDateFilter.calendarOpen = false;
    this.generationTrendDateFilter.calendarOpen = false;
    this.energyExchangeDateFilter.calendarOpen = false;
    this.provinceDateFilter.calendarOpen = false;
    void this.syncDateFilterLabels();
    this.cdr.markForCheck();
  }

  protected getMixDateLabel(type: DashboardDateFilterType): string {
    const filter = this.getMixDateFilter(type);
    return this.isBsDateMode ? filter.bsLabel || filter.date : filter.date;
  }

  protected openMixDatePicker(type: DashboardDateFilterType): void {
    if (!this.isBsDateMode) {
      return;
    }

    const filter = this.getMixDateFilter(type);
    if (filter.calendarOpen) {
      filter.calendarOpen = false;
      return;
    }

    filter.calendarOpen = true;
    setTimeout(() => this.positionOpenMixCalendar(type));
    void this.loadBsMixCalendar(type, filter.date, 'AD');
  }

  protected closeMixDatePicker(type: DashboardDateFilterType): void {
    this.getMixDateFilter(type).calendarOpen = false;
  }

  private positionOpenMixCalendar(type?: DashboardDateFilterType): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const activeType = type ?? (['source', 'owner', 'load', 'trend', 'exchange', 'province'] as DashboardDateFilterType[])
      .find((candidate) => this.getMixDateFilter(candidate).calendarOpen);
    if (!activeType) {
      return;
    }

    const input = document.getElementById(this.getMixDateInputId(activeType));
    const calendar = document.querySelector<HTMLElement>('.generation-mix-bs-calendar[open]');
    if (!input || !calendar) {
      return;
    }

    const inputRect = input.getBoundingClientRect();
    const calendarWidth = calendar.offsetWidth || 320;
    const calendarHeight = calendar.offsetHeight || 320;
    const horizontalPadding = 8;
    const left = Math.max(
      horizontalPadding,
      Math.min(inputRect.left, window.innerWidth - calendarWidth - horizontalPadding),
    );
    const belowTop = inputRect.bottom + 6;
    const top = belowTop + calendarHeight <= window.innerHeight - horizontalPadding
      ? belowTop
      : Math.max(horizontalPadding, inputRect.top - calendarHeight - 6);

    calendar.style.left = `${left}px`;
    calendar.style.top = `${top}px`;
    calendar.style.right = 'auto';
    calendar.style.transform = 'none';
  }

  private getMixDateInputId(type: DashboardDateFilterType): string {
    const inputIdByType: Record<DashboardDateFilterType, string> = {
      source: 'source-generation-date',
      owner: 'owner-generation-date',
      load: 'load-curve-date',
      trend: 'generation-trend-date',
      exchange: 'energy-exchange-date',
      province: 'province-load-date',
    };
    return inputIdByType[type];
  }

  protected handleMixAdDateChange(type: DashboardDateFilterType, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (!value) {
      return;
    }

    this.getMixDateFilter(type).date = value;
    void this.syncMixDateLabel(type);
    this.loadDateFilteredCard(type);
  }

  protected selectBsMixDate(type: DashboardDateFilterType, cell: BsCalendarCell): void {
    if (!cell.ad_date) {
      return;
    }

    const filter = this.getMixDateFilter(type);
    filter.date = cell.ad_date;
    filter.bsLabel = this.formatBsDateLabel(cell.bs_date);
    this.closeMixDatePicker(type);
    this.loadDateFilteredCard(type);
  }

  protected shiftBsMixCalendarMonth(type: DashboardDateFilterType, offset: number): void {
    const filter = this.getMixDateFilter(type);
    if (!filter.calendar || !offset) {
      return;
    }

    let month = filter.calendar.bs_month + offset;
    let year = filter.calendar.bs_year;
    if (month < 1) {
      month = 12;
      year -= 1;
    } else if (month > 12) {
      month = 1;
      year += 1;
    }
    void this.loadBsMixCalendar(type, `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-01`, 'BS');
  }

  protected selectVoltageFilter(filter: VoltageFilter): void {
    this.voltageFilter = filter;
  }
  protected handleMenuToggle(): void {
    if (window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }
  protected percentage(value: number, slices: DonutSlice[]): string {
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);
    return total > 0 ? `${((value / total) * 100).toFixed(1)}%` : '0.0%';
  }

  protected voltageClass(kv: number): string {
    return `voltage-badge--${kv}`;
  }

  protected displayKpiValue(value: GridKpiCard['value']): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    if (typeof value === 'string' && this.isBsDateMode) {
      const match = /^(\d{2})-(\d{2})-(\d{4})(?:\s.*)?$/.exec(value);
      if (match) {
        const adDate = `${match[3]}-${match[2]}-${match[1]}`;
        const bsDate = this.bsDateLabels.get(adDate);
        if (bsDate) {
          return `${bsDate}${match[4] ?? ''}`;
        }
      }
    }

    return typeof value === 'number'
      ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)
      : value;
  }

  protected formatMarketDate(value: string | null | undefined): string {
    const parts = value?.split('-');
    if (!parts || parts.length !== 3) {
      return '-';
    }

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = Number(parts[1]);
    if (this.isBsDateMode) {
      return this.bsDateLabels.get(value ?? '') ?? '-';
    }

    return `${parts[2]} ${monthNames[month - 1] ?? parts[1]} ${parts[0]}`;
  }

  protected formatCapacityMixLegend(slice: DonutSlice): string {
    const percentage = slice.percentage ?? this.getPercentageValue(slice.value, this.capacityMix);
    return `${this.formatCapacityValue(slice.value)} MW (${percentage.toFixed(0)}%)`;
  }

  private getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private createMixDateFilter(): DailyGenerationDateFilter {
    return { date: this.getTodayDate(), bsLabel: '', calendarOpen: false, calendarLoading: false, calendar: null };
  }

  private getMixDateFilter(type: DashboardDateFilterType): DailyGenerationDateFilter {
    if (type === 'source') {
      return this.sourceMixDateFilter;
    }
    if (type === 'owner') {
      return this.ownerMixDateFilter;
    }
    if (type === 'load') {
      return this.loadCurveDateFilter;
    }
    if (type === 'trend') {
      return this.generationTrendDateFilter;
    }
    return type === 'exchange' ? this.energyExchangeDateFilter : this.provinceDateFilter;
  }

  private formatBsDateLabel(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const normalized = value.trim().replace(/\//g, '-');
    const parts = normalized.split('-');
    if (parts.length !== 3) {
      return normalized;
    }

    if (parts[0].length === 4) {
      return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0]}`;
    }

    return `${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[2]}`;
  }

  private async syncDateFilterLabels(): Promise<void> {
    await Promise.all([
      this.syncMixDateLabel('source'),
      this.syncMixDateLabel('owner'),
      this.syncMixDateLabel('load'),
      this.syncMixDateLabel('trend'),
      this.syncMixDateLabel('exchange'),
      this.syncMixDateLabel('province'),
    ]);
  }

  private async syncMixDateLabel(type: DashboardDateFilterType): Promise<void> {
    const filter = this.getMixDateFilter(type);
    if (!this.isBsDateMode) {
      filter.bsLabel = '';
      this.cdr.markForCheck();
      return;
    }

    try {
      const response = await firstValueFrom(this.httpSvc.requestCall(
        AuthEndPoints.CONVERT_LOG_SHEET_DATE,
        ApiMethod.POST,
        { data_date: filter.date, bs_ad_key: 'BS', source_bs_ad_key: 'AD' },
        false,
        { suppressToast: true },
      ));
      const value = response?.value;
      filter.bsLabel = this.formatBsDateLabel(value?.bs_date) || this.formatBsDateLabel(value?.selected_date_label) || filter.date;
    } catch {
      filter.bsLabel = filter.date;
    }
    this.cdr.markForCheck();
  }

  private async loadBsMixCalendar(type: DashboardDateFilterType, dateValue: string, bsAdKey: 'AD' | 'BS'): Promise<void> {
    const filter = this.getMixDateFilter(type);
    filter.calendarLoading = true;
    try {
      const response = await firstValueFrom(this.httpSvc.requestCall(
        AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR,
        ApiMethod.POST,
        { data_date: dateValue, bs_ad_key: bsAdKey },
        false,
        { suppressToast: true },
      ));
      filter.calendar = response?.value ?? null;
    } catch {
      filter.calendar = null;
    } finally {
      filter.calendarLoading = false;
      this.cdr.markForCheck();
    }
  }

  private loadInitialDashboardCards(): void {
    this.loadGridKpis();
    this.loadInstalledCapacityMix();
    this.loadDailyGenerationSourceMix();
    this.loadDailyGenerationOwnerMix();
  }

  private observeDeferredDashboardSections(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.loadDeferredDashboardCards();
      return;
    }

    const sectionLoaders = new Map<HTMLElement, () => void>();
    if (this.loadDemandSectionElement) {
      sectionLoaders.set(this.loadDemandSectionElement.nativeElement, () => {
        this.loadLoadCurve();
        this.loadProvinceLoadDistribution();
      });
    }
    if (this.generationEnergySectionElement) {
      sectionLoaders.set(this.generationEnergySectionElement.nativeElement, () => {
        this.loadGenerationTrend();
        this.loadDailyEnergyExchange();
      });
    }
    if (this.transmissionSectionElement) {
      sectionLoaders.set(this.transmissionSectionElement.nativeElement, () => {
        this.loadTransmissionLinesLoading();
        this.loadCrossborderLineExchangeDetails();
      });
    }

    this.dashboardSectionObserver = new IntersectionObserver((entries) => {
      entries.filter((entry) => entry.isIntersecting).forEach((entry) => {
        const loadSection = sectionLoaders.get(entry.target as HTMLElement);
        loadSection?.();
        this.dashboardSectionObserver?.unobserve(entry.target);
      });
    }, { rootMargin: '200px 0px' });
    sectionLoaders.forEach((_loadSection, section) => this.dashboardSectionObserver?.observe(section));
  }

  private loadDeferredDashboardCards(): void {
    this.loadLoadCurve();
    this.loadProvinceLoadDistribution();
    this.loadGenerationTrend();
    this.loadDailyEnergyExchange();
    this.loadTransmissionLinesLoading();
    this.loadCrossborderLineExchangeDetails();
  }

  private loadDateFilteredCard(type: DashboardDateFilterType): void {
    switch (type) {
      case 'source': this.loadDailyGenerationSourceMix(true); break;
      case 'owner': this.loadDailyGenerationOwnerMix(true); break;
      case 'load': this.loadLoadCurve(true); break;
      case 'trend': this.loadGenerationTrend(true); break;
      case 'exchange': this.loadDailyEnergyExchange(true); break;
      case 'province': this.loadProvinceLoadDistribution(true); break;
    }
  }

  private loadGridKpis(): void {
    this.loadAdminCard<GridKpiOverview>(AuthEndPoints.GET_ADMIN_GRID_KPIS, (overview) => {
      this.gridKpiTitle = overview.title;
      this.gridKpiDataDate = overview.data_date ?? '';
      this.gridKpiUpdatedTime = this.toTimeLabel(overview.updated_at);
      this.kpis = overview.cards;
      void this.syncDashboardBsDateLabels();
      this.updateGridKpiDateLabel();
    });
  }

  private loadInstalledCapacityMix(): void {
    this.loadAdminCard<InstalledCapacityMix>(AuthEndPoints.GET_ADMIN_INSTALLED_CAPACITY_MIX, (mix) => this.applyInstalledCapacityMix(mix));
  }

  private loadDailyGenerationSourceMix(forceRefresh = false): void {
    this.loadAdminCard<DailyGenerationSourceMix>(AuthEndPoints.GET_ADMIN_DAILY_GENERATION_SOURCE_MIX, (mix) => this.applyDailyGenerationSourceMix(mix), this.dateQuery(this.sourceMixDateFilter.date), forceRefresh);
  }

  private loadDailyGenerationOwnerMix(forceRefresh = false): void {
    this.loadAdminCard<DailyGenerationOwnerMix>(AuthEndPoints.GET_ADMIN_DAILY_GENERATION_OWNER_MIX, (mix) => this.applyDailyGenerationOwnerMix(mix), this.dateQuery(this.ownerMixDateFilter.date), forceRefresh);
  }

  private loadLoadCurve(forceRefresh = false): void {
    this.loadAdminCard<LoadCurve>(AuthEndPoints.GET_ADMIN_LOAD_CURVE, (curve) => this.applyLoadCurve(curve), this.dateQuery(this.loadCurveDateFilter.date), forceRefresh);
  }

  private loadGenerationTrend(forceRefresh = false): void {
    this.loadAdminCard<GenerationTrend>(AuthEndPoints.GET_ADMIN_GENERATION_TREND, (trend) => this.applyGenerationTrend(trend), this.dateQuery(this.generationTrendDateFilter.date), forceRefresh);
  }

  private loadDailyEnergyExchange(forceRefresh = false): void {
    this.loadAdminCard<DailyEnergyExchange>(AuthEndPoints.GET_ADMIN_DAILY_ENERGY_EXCHANGE, (exchange) => this.applyDailyEnergyExchange(exchange), this.dateQuery(this.energyExchangeDateFilter.date), forceRefresh);
  }

  private loadProvinceLoadDistribution(forceRefresh = false): void {
    this.loadAdminCard<ProvinceLoadDistribution>(AuthEndPoints.GET_ADMIN_PROVINCE_LOAD_DISTRIBUTION, (distribution) => this.applyProvinceLoadDistribution(distribution), this.dateQuery(this.provinceDateFilter.date), forceRefresh);
  }

  private loadTransmissionLinesLoading(): void {
    this.loadAdminCard<TransmissionLinesLoading>(AuthEndPoints.GET_ADMIN_TRANSMISSION_LINES_LOADING, (loading) => this.applyTransmissionLinesLoading(loading));
  }

  private loadCrossborderLineExchangeDetails(): void {
    this.loadAdminCard<CrossborderLineExchangeDetails>(AuthEndPoints.GET_ADMIN_CROSSBORDER_LINE_EXCHANGE_DETAILS, (details) => this.applyCrossborderLineExchangeDetails(details));
  }

  private loadAdminCard<T>(endpoint: string, apply: (value: T) => void, query = '', forceRefresh = false): void {
    const requestKey = `${endpoint}${query}`;
    const pendingRequestId = this.pendingDashboardRequestIds.get(requestKey);
    if (pendingRequestId !== undefined) {
      this.latestDashboardRequestIds.set(endpoint, pendingRequestId);
      return;
    }
    if (!forceRefresh && this.loadedDashboardRequests.has(requestKey)) {
      return;
    }

    const requestId = ++this.nextDashboardRequestId;
    this.latestDashboardRequestIds.set(endpoint, requestId);
    this.pendingDashboardRequestIds.set(requestKey, requestId);
    this.httpSvc.requestCall(endpoint, ApiMethod.GET, query, false, { suppressToast: true }).subscribe({
      next: (response: AdminDashboardApiResponse<T>) => {
        if (response?.value) {
          if (this.latestDashboardRequestIds.get(endpoint) === requestId) {
            apply(response.value);
          }
          this.loadedDashboardRequests.add(requestKey);
        }
        this.pendingDashboardRequestIds.delete(requestKey);
        this.cdr.markForCheck();
      },
      error: () => {
        this.pendingDashboardRequestIds.delete(requestKey);
        this.cdr.markForCheck();
      },
    });
  }

  private async syncDashboardBsDateLabels(): Promise<void> {
    const dates = new Set<string>();
    if (this.gridKpiDataDate) {
      dates.add(this.gridKpiDataDate);
    }
    this.kpis.forEach((kpi) => {
      if (kpi.market_date) {
        dates.add(kpi.market_date);
      }
      const match = typeof kpi.value === 'string'
        ? /^(\d{2})-(\d{2})-(\d{4})(?:\s|T)/.exec(kpi.value)
        : null;
      if (match) {
        dates.add(`${match[3]}-${match[2]}-${match[1]}`);
      }
    });

    await Promise.all([...dates].map(async (adDate) => {
      try {
        const response = await firstValueFrom(this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_LOG_SHEET_DATE,
          ApiMethod.POST,
          { data_date: adDate, bs_ad_key: 'BS', source_bs_ad_key: 'AD' },
          false,
          { suppressToast: true },
        ));
        const label = response?.value?.selected_date_label || response?.value?.bs_date_label;
        if (label) {
          this.bsDateLabels.set(adDate, this.formatBsDateLabel(response?.value?.bs_date) || this.formatBsDateLabel(label));
        }
      } catch {
        // Keep the AD value when a BS label cannot be resolved.
      }
    }));
    this.updateGridKpiDateLabel();
    this.cdr.markForCheck();
  }

  private dateQuery(dataDate: string): string {
    return `?data_date=${encodeURIComponent(dataDate)}&bs_ad_key=AD`;
  }

  private updateGridKpiDateLabel(): void {
    const currentTime = this.serverTimeSvc.getCurrentTimeLabel();
    const separatorIndex = currentTime.lastIndexOf(' ');
    const timeSuffix = separatorIndex >= 0 ? currentTime.slice(separatorIndex + 1) : '';
    const dateLabel = this.isClockTime(timeSuffix) ? currentTime.slice(0, separatorIndex) : currentTime;
    this.gridKpiDateLabel = dateLabel || (this.isBsDateMode
      ? this.bsDateLabels.get(this.gridKpiDataDate) ?? this.gridKpiDataDate
      : this.gridKpiDataDate);
  }

  private isClockTime(value: string): boolean {
    return value.length === 8
      && value[2] === ':'
      && value[5] === ':'
      && Number.isInteger(Number(value.slice(0, 2)))
      && Number.isInteger(Number(value.slice(3, 5)))
      && Number.isInteger(Number(value.slice(6, 8)));
  }

  private toTimeLabel(value: string | null): string {
    if (!value) {
      return '';
    }

    const match = /(?:T|\s)(\d{2}:\d{2}:\d{2})/.exec(value);
    return match?.[1] ?? '';
  }

  private renderCharts(): void {
    this.renderMixCharts();
    this.renderLoadCurveChart();
    this.renderGenerationChart();
    this.renderExchangeChart();
  }

  private renderMixCharts(): void {
    this.renderCapacityMixChart();
    this.renderDailyGenerationSourceMixChart();
    this.renderDailyGenerationOwnerMixChart();
  }

  private renderLoadCurveChart(): void {
    if (this.loadCurveChart) {
      this.loadCurveChart.destroy();
      this.charts.splice(this.charts.indexOf(this.loadCurveChart), 1);
      this.loadCurveChart = undefined;
    }

    if (!this.loadChartElement || !this.loadCurve.categories.length) {
      return;
    }

    this.loadCurveChart = Highcharts.chart(this.loadChartElement.nativeElement, this.lineChartOptions(
      this.loadCurve.categories,
      [
        { type: 'line', name: 'National Demand', data: this.loadCurve.national_demand, color: '#3b82f6' },
        { type: 'line', name: 'System Demand', data: this.loadCurve.system_demand, color: '#f59e0b', dashStyle: 'ShortDash' },
      ],
    ));
    this.charts.push(this.loadCurveChart);
  }

  private renderGenerationChart(): void {
    if (this.generationTrendChart) {
      this.generationTrendChart.destroy();
      this.charts.splice(this.charts.indexOf(this.generationTrendChart), 1);
      this.generationTrendChart = undefined;
    }

    if (!this.generationChartElement || !this.generationTrend.categories.length) {
      return;
    }

    const series = this.generationTrend.series.map((item) => ({
      type: 'area' as const,
      name: item.name,
      data: item.values,
      color: DAILY_GENERATION_SOURCE_COLORS[item.name] ?? DAILY_GENERATION_SOURCE_COLORS['Others'],
    }));
    this.generationTrendChart = Highcharts.chart(
      this.generationChartElement.nativeElement,
      this.areaOptions(this.generationTrend.categories, series),
    );
    this.charts.push(this.generationTrendChart);
  }

  private renderExchangeChart(): void {
    if (this.energyExchangeChart) {
      this.energyExchangeChart.destroy();
      this.charts.splice(this.charts.indexOf(this.energyExchangeChart), 1);
      this.energyExchangeChart = undefined;
    }

    if (!this.exchangeChartElement || !this.dailyEnergyExchange.categories.length) {
      return;
    }

    this.energyExchangeChart = Highcharts.chart(
      this.exchangeChartElement.nativeElement,
      this.areaOptions(this.dailyEnergyExchange.categories, [
        { type: 'area', name: 'Export', data: this.dailyEnergyExchange.export, color: '#f59e0b' },
        { type: 'area', name: 'Import', data: this.dailyEnergyExchange.import, color: '#10b981' },
        { type: 'area', name: 'Net', data: this.dailyEnergyExchange.net, color: '#3b82f6' },
      ]),
    );
    this.charts.push(this.energyExchangeChart);
  }

  private renderDonut(
    element: ElementRef<HTMLDivElement> | undefined,
    data: DonutSlice[],
    total: string,
    unit: string,
  ): void {
    const chart = this.createDonut(element, data, total, unit);
    if (chart) {
      this.charts.push(chart);
    }
  }

  private renderCapacityMixChart(): void {
    if (this.capacityMixChart) {
      this.capacityMixChart.destroy();
      this.charts.splice(this.charts.indexOf(this.capacityMixChart), 1);
      this.capacityMixChart = undefined;
    }

    const chart = this.createDonut(
      this.capacityChartElement,
      this.capacityMix,
      this.formatCapacityValue(this.installedCapacityTotalMw),
      'MW Total',
    );
    if (chart) {
      this.capacityMixChart = chart;
      this.charts.push(chart);
    }
  }

  private renderDailyGenerationSourceMixChart(): void {
    if (this.dailyGenerationSourceMixChart) {
      this.dailyGenerationSourceMixChart.destroy();
      this.charts.splice(this.charts.indexOf(this.dailyGenerationSourceMixChart), 1);
      this.dailyGenerationSourceMixChart = undefined;
    }

    const chart = this.createDonut(
      this.fuelChartElement,
      this.dailyGenerationSourceMix,
      this.formatCapacityValue(this.dailyGenerationSourceTotalMw),
      MEGAWATT_UNIT,
    );
    if (chart) {
      this.dailyGenerationSourceMixChart = chart;
      this.charts.push(chart);
    }
  }

  private renderDailyGenerationOwnerMixChart(): void {
    if (this.dailyGenerationOwnerMixChart) {
      this.dailyGenerationOwnerMixChart.destroy();
      this.charts.splice(this.charts.indexOf(this.dailyGenerationOwnerMixChart), 1);
      this.dailyGenerationOwnerMixChart = undefined;
    }

    const chart = this.createDonut(
      this.sourceChartElement,
      this.dailyGenerationOwnerMix,
      this.formatCapacityValue(this.dailyGenerationOwnerTotalMw),
      MEGAWATT_UNIT,
    );
    if (chart) {
      this.dailyGenerationOwnerMixChart = chart;
      this.charts.push(chart);
    }
  }

  private createDonut(
    element: ElementRef<HTMLDivElement> | undefined,
    data: DonutSlice[],
    total: string,
    unit: string,
  ): Highcharts.Chart | undefined {
    if (!element || !data.length) {
      return undefined;
    }

    let totalFontSize = 18;
    if (total.length > 9) {
      totalFontSize = 13;
    } else if (total.length > 7) {
      totalFontSize = 15;
    }
    return Highcharts.chart(element.nativeElement, {
      chart: { type: 'pie', backgroundColor: 'transparent', height: 170, spacing: [0, 0, 0, 0], style: { fontFamily: 'var(--app-font-sans)' } },
      title: {
        text: `<div style="display:flex;min-width:96px;flex-direction:column;align-items:center;justify-content:center;gap:2px;text-align:center;line-height:1"><span style="display:block;max-width:96px;overflow:visible;white-space:nowrap;font-size:${totalFontSize}px;font-weight:800;color:#0f172a">${total}</span><span style="display:block;color:#64748b;font-size:10px;font-weight:600;white-space:nowrap">${unit}</span></div>`,
        align: 'center', verticalAlign: 'middle', x: 0, y: 0, useHTML: true,
        style: { textAlign: 'center' },
      },
      tooltip: this.getDashboardTooltip({ pointFormat: '<b>{point.y:,.0f} MW</b> ({point.percentage:.1f}%)' }),
      credits: { enabled: false },
      legend: { enabled: false },
      plotOptions: { pie: { innerSize: '68%', size: '90%', borderWidth: 0, dataLabels: { enabled: false } } },
      series: [{ type: 'pie', data: data.map((slice) => ({ name: slice.name, y: slice.value, color: slice.color })) }],
    });
  }

  private applyInstalledCapacityMix(mix: InstalledCapacityMix | undefined): void {
    if (!mix) {
      return;
    }

    this.installedCapacityTotalMw = mix.total_mw;
    this.capacityMix = mix.categories.map((category) => ({
      name: category.name,
      value: category.capacity_mw,
      percentage: category.percentage,
      color: CAPACITY_MIX_COLORS[category.name] ?? CAPACITY_MIX_COLORS['Other'],
    }));
    if (this.viewInitialized) {
      this.renderCapacityMixChart();
    }
  }

  private applyDailyGenerationSourceMix(mix: DailyGenerationSourceMix | undefined): void {
    if (!mix) {
      return;
    }

    this.dailyGenerationSourceTotalMw = mix.total_mw;
    this.dailyGenerationSourceMix = mix.categories.map((category) => ({
      name: category.name,
      value: category.generation_mw,
      percentage: category.percentage,
      color: DAILY_GENERATION_SOURCE_COLORS[category.name] ?? DAILY_GENERATION_SOURCE_COLORS['Others'],
    }));
    if (this.viewInitialized) {
      this.renderDailyGenerationSourceMixChart();
    }
  }

  private applyDailyGenerationOwnerMix(mix: DailyGenerationOwnerMix | undefined): void {
    if (!mix) {
      return;
    }

    this.dailyGenerationOwnerTotalMw = mix.total_mw;
    this.dailyGenerationOwnerMix = mix.categories.map((category) => ({
      name: category.name,
      value: category.generation_mw,
      percentage: category.percentage,
      color: DAILY_GENERATION_OWNER_COLORS[category.name] ?? DAILY_GENERATION_OWNER_COLORS['IPP'],
    }));
    if (this.viewInitialized) {
      this.renderDailyGenerationOwnerMixChart();
    }
  }

  private applyGenerationTrend(trend: GenerationTrend | undefined): void {
    if (!trend) {
      return;
    }

    this.generationTrend = trend;
    if (this.viewInitialized) {
      this.renderGenerationChart();
    }
  }

  private applyDailyEnergyExchange(exchange: DailyEnergyExchange | undefined): void {
    if (!exchange) {
      return;
    }

    this.dailyEnergyExchange = exchange;
    if (this.viewInitialized) {
      this.renderExchangeChart();
    }
  }

  private applyLoadCurve(loadCurve: LoadCurve | undefined): void {
    if (!loadCurve) {
      return;
    }

    const lastSyncedBlock = Number(loadCurve.last_synced_block_no ?? 0);
    const blockNumbers = loadCurve.block_numbers?.length
      ? loadCurve.block_numbers
      : loadCurve.categories.map((_, index) => index + 1);
    const displayedPointIndexes = lastSyncedBlock
      ? blockNumbers.reduce<number[]>((indexes, blockNo, index) => (blockNo <= lastSyncedBlock ? [...indexes, index] : indexes), [])
      : blockNumbers.map((_, index) => index);
    this.loadCurve = {
      ...loadCurve,
      block_numbers: displayedPointIndexes.map((index) => blockNumbers[index]),
      categories: displayedPointIndexes.map((index) => loadCurve.categories[index]),
      national_demand: displayedPointIndexes.map((index) => loadCurve.national_demand[index]),
      system_demand: displayedPointIndexes.map((index) => loadCurve.system_demand[index]),
    };
    if (this.viewInitialized) {
      this.renderLoadCurveChart();
    }
  }

  private applyProvinceLoadDistribution(distribution: ProvinceLoadDistribution | undefined): void {
    if (!distribution) {
      return;
    }

    this.provinces = distribution.provinces;
    this.provinceTotalSystemLoadMw = distribution.total_system_load_mw;
  }

  private applyTransmissionLinesLoading(loading: TransmissionLinesLoading | undefined): void {
    if (!loading) {
      return;
    }

    this.voltageFilters = ['all', ...loading.voltage_levels];
    if (this.voltageFilter !== 'all' && !loading.voltage_levels.includes(this.voltageFilter)) {
      this.voltageFilter = 'all';
    }
    this.transmissionLines = loading.lines.map((line) => ({
      id: line.id,
      line: line.line,
      kv: line.kv,
      from: line.from,
      to: line.to,
      mwFlow: line.mw_flow,
    }));
  }

  private applyCrossborderLineExchangeDetails(details: CrossborderLineExchangeDetails | undefined): void {
    if (!details) {
      return;
    }

    this.tieLines = details.lines.map((line) => ({
      name: line.interconnection,
      voltage: line.voltage,
      direction: line.direction,
      actual: line.mw_flow,
      variance: 0,
    }));
  }

  protected formatCapacityValue(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  }

  private getPercentageValue(value: number, slices: DonutSlice[]): number {
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);
    return total > 0 ? (value / total) * 100 : 0;
  }

  private lineChartOptions(
    categories: string[],
    series: Highcharts.SeriesLineOptions[],
  ): Highcharts.Options {
    return {
      chart: { type: 'spline', backgroundColor: 'transparent', height: CHART_HEIGHT, style: { fontFamily: 'var(--app-font-sans)' } },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: { categories, tickInterval: 3, lineColor: '#cbd5e1', labels: { style: { color: '#64748b', fontSize: '11px' } } },
      yAxis: { title: { text: MEGAWATT_UNIT, style: { color: '#64748b' } }, gridLineDashStyle: 'Dash', gridLineColor: '#e2e8f0', labels: { style: { color: '#64748b' } } },
      legend: { align: 'center', verticalAlign: 'bottom', itemStyle: { color: '#475569', fontSize: '12px' }, itemHoverStyle: { color: '#475569' } },
      tooltip: this.getDashboardTooltip({ shared: true, valueSuffix: ' MW' }),
      plotOptions: { spline: { lineWidth: 2, marker: { enabled: false } } },
      series,
    };
  }

  private areaOptions(categories: string[], series: Highcharts.SeriesAreaOptions[]): Highcharts.Options {
    return {
      chart: { type: 'area', backgroundColor: 'transparent', height: CHART_HEIGHT, style: { fontFamily: 'var(--app-font-sans)' } },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: { categories, tickInterval: 4, lineColor: '#cbd5e1', labels: { style: { color: '#64748b', fontSize: '11px' } } },
      yAxis: { title: { text: MEGAWATT_UNIT, style: { color: '#64748b' } }, gridLineDashStyle: 'Dash', gridLineColor: '#e2e8f0', labels: { style: { color: '#64748b' } } },
      legend: { align: 'center', verticalAlign: 'bottom', itemStyle: { color: '#475569', fontSize: '12px' }, itemHoverStyle: { color: '#475569' } },
      tooltip: this.getDashboardTooltip({ shared: true, valueDecimals: 2, valueSuffix: ' MW' }),
      plotOptions: { area: { stacking: 'normal', lineWidth: 1.5, fillOpacity: 0.45, marker: { enabled: false } } },
      series,
    };
  }

  private getDashboardTooltip(options: Highcharts.TooltipOptions): Highcharts.TooltipOptions {
    return {
      ...options,
      backgroundColor: '#ffffff',
      borderColor: '#e2e8f0',
      borderRadius: 8,
      shadow: true,
      style: { ...options.style, color: '#000000' },
    };
  }
}
